import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

export interface QuoteItem {
  productId: string;
  description: string;
  quantity: number;
  unitValue: number;
  discount: number;
  discountType: 'PERCENTAGE' | 'FIXED';
  taxValues: Record<string, number>;
  total: number;
}

export interface Quote {
  id: string;
  tenantId: string;
  customerId: string;
  /* `customerName` nao vem na resposta: o nome do cliente e resolvido pela
     tela a partir de `customerId`, como nas demais listagens. */
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'EXPIRED' | 'REJECTED';
  items: QuoteItem[];
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  conditions: string;
  validityDays: number;
  validUntil: string;
  acceptedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteInput {
  customerId: string;
  items: {
    productId: string;
    description: string;
    quantity: number;
    unitValue: number;
    discount?: number;
    discountType?: 'PERCENTAGE' | 'FIXED';
    taxValues?: Record<string, number>;
  }[];
  conditions?: string;
  validityDays: number;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export function useQuotes(params?: {
  customerId?: string;
  status?: string;
  page?: number;
  limit?: number;
}): ReturnType<typeof useQuery<PaginatedResponse<Quote>>> {
  return useQuery<PaginatedResponse<Quote>>({
    queryKey: [...queryKeys.sales.quotes(), params],
    queryFn: async () =>
      apiClient.get<PaginatedResponse<Quote>>('/quotes', {
        params: {
          ...(params?.customerId ? { customerId: params.customerId } : {}),
          ...(params?.status ? { status: params.status } : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
        },
      }),
  });
}

export function useQuote(id: string): ReturnType<typeof useQuery<Quote>> {
  return useQuery<Quote>({
    queryKey: [...queryKeys.sales.quotes(), id],
    queryFn: async () => apiClient.get<Quote>(`/quotes/${id}`),
    enabled: !!id,
  });
}

export function useCreateQuote() {
  const queryClient = useQueryClient();

  return useMutation<Quote, unknown, QuoteInput, unknown>({
    mutationFn: (input: QuoteInput) => apiClient.post<Quote>('/quotes', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sales.quotes() });
    },
  });
}

export function useSendQuote() {
  const queryClient = useQueryClient();

  return useMutation<Quote, unknown, string, unknown>({
    mutationFn: (id: string) => apiClient.patch<Quote>(`/quotes/${id}/send`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sales.quotes() });
    },
  });
}

export function useAcceptQuote() {
  const queryClient = useQueryClient();

  return useMutation<Quote, unknown, string, unknown>({
    mutationFn: (id: string) => apiClient.patch<Quote>(`/quotes/${id}/accept`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sales.quotes() });
    },
  });
}

export function useRejectQuote() {
  const queryClient = useQueryClient();

  return useMutation<Quote, unknown, { id: string; reason: string }, unknown>({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.patch<Quote>(`/quotes/${id}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sales.quotes() });
    },
  });
}

export function useConvertQuoteToOrder() {
  const queryClient = useQueryClient();

  return useMutation<{ orderId: string; message: string }, unknown, string, unknown>({
    mutationFn: (id: string) =>
      apiClient.post<{ orderId: string; message: string }>(`/quotes/${id}/convert`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sales.quotes() });
      // A conversão cria um pedido: invalida as listas de vendas em cache.
      // Sem tenantId neste hook — predicado restrito às chaves sales/list.
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'tenant' &&
          query.queryKey[2] === 'sales' &&
          query.queryKey[3] === 'list',
      });
    },
  });
}

export function useDeleteQuote() {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, string, unknown>({
    mutationFn: (id: string) => apiClient.delete(`/quotes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sales.quotes() });
    },
  });
}
